import React, { useCallback, useEffect, useState } from "react";
import { chamarAdmin, mensagemDeErro } from "../../lib/adminApi";

// Aba "Integrações" (só admins; o servidor também confere: api/admin-integracoes.ts).
// - Tokens da API de dúvidas (GET /api/v1/faq), para o servidor de uma landing page. O token
//   aparece uma vez só, ao ser criado.
// - Sites onde a central roda embutida (bolha, painéis) ou o widget de dúvidas. Qualquer site
//   funciona; site novo vira aviso no Slack e aqui se bloqueia o que não for nosso.

interface Token {
  id: string;
  nome: string;
  prefixo: string;
  ativo: boolean;
  criado_por: string | null;
  usos: string;
  criado_em: string;
  ultimo_uso: string | null;
  revogado_em: string | null;
}

interface Site {
  dominio: string;
  bloqueado: boolean;
  bloqueado_por: string | null;
  primeiro_uso: string;
  ultimo_uso: string;
  conversas: number;
}

const ROTA = "/api/admin-integracoes";

const AdminIntegracoes: React.FC = () => {
  const [tokens, setTokens] = useState<Token[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [nome, setNome] = useState("");
  const [novo, setNovo] = useState<{ nome: string; token: string } | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const atualizar = useCallback(async () => {
    const [t, s] = await Promise.all([chamarAdmin(ROTA, { acao: "tokens" }), chamarAdmin(ROTA, { acao: "sites" })]);
    if (t.ok) setTokens(t.json.tokens);
    else setErro(mensagemDeErro(t.json?.erro));
    if (s.ok) setSites(s.json.sites);
  }, []);

  useEffect(() => {
    atualizar();
  }, [atualizar]);

  const criarToken = async () => {
    setErro(null);
    setOcupado(true);
    try {
      const r = await chamarAdmin(ROTA, { acao: "criar-token", nome });
      if (!r.ok) return setErro(mensagemDeErro(r.json?.erro));
      setNovo({ nome, token: r.json.token });
      setCopiado(false);
      setNome("");
      await atualizar();
    } finally {
      setOcupado(false);
    }
  };

  const revogar = async (t: Token) => {
    if (!window.confirm(`Revogar o token "${t.nome}"? A integração que usa esse token para de receber os dados em até 1 minuto.`)) return;
    setErro(null);
    const r = await chamarAdmin(ROTA, { acao: "revogar-token", id: t.id });
    if (!r.ok) return setErro(mensagemDeErro(r.json?.erro));
    await atualizar();
  };

  const alternarBloqueio = async (s: Site) => {
    const bloquear = !s.bloqueado;
    if (bloquear && !window.confirm(`Bloquear ${s.dominio}? A central e o widget de dúvidas deixam de funcionar nesse site em até 1 minuto.`)) return;
    setErro(null);
    const r = await chamarAdmin(ROTA, { acao: "bloquear", dominio: s.dominio, bloqueado: bloquear });
    if (!r.ok) return setErro(mensagemDeErro(r.json?.erro));
    await atualizar();
  };

  const copiar = async () => {
    if (!novo) return;
    try {
      await navigator.clipboard.writeText(novo.token);
      setCopiado(true);
    } catch {
      setCopiado(false);
    }
  };

  return (
    <>
      <section className="metricas-bloco">
        <h2>Tokens da API de dúvidas</h2>
        <p className="metricas-legenda">
          Para o dev que monta a seção de dúvidas de uma landing page com a API (GET /api/v1/faq). O token é para o servidor
          do site: no código da página ele ficaria visível. Um token por integração, para poder revogar só aquela.
        </p>
        <form
          className="usuarios-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (nome.trim()) criarToken();
          }}
        >
          <input placeholder='Nome da integração (ex.: "LP do Sak · dev Fulano")' value={nome} onChange={(e) => setNome(e.target.value)} />
          <button type="submit" disabled={ocupado || !nome.trim()}>
            Criar token
          </button>
        </form>
        {erro && <p className="metricas-erro">{erro}</p>}
        {novo && (
          <div className="usuarios-link">
            <p>
              Token de <strong>{novo.nome}</strong>. Ele aparece só agora: copie e envie para o dev por um canal privado.
            </p>
            <div className="usuarios-link-linha">
              <input readOnly value={novo.token} onFocus={(e) => e.target.select()} />
              <button type="button" onClick={copiar}>
                {copiado ? "Copiado" : "Copiar"}
              </button>
              <button type="button" className="usuarios-fechar" onClick={() => setNovo(null)}>
                Fechar
              </button>
            </div>
          </div>
        )}
        <table className="metricas-tabela">
          <thead>
            <tr>
              <th>Integração</th>
              <th>Último uso</th>
              <th>Pedidos</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.id} className={t.ativo ? "" : "usuarios-inativo"}>
                <td>
                  {t.nome}
                  {!t.ativo && <span className="metricas-selo">revogado {t.revogado_em}</span>}
                  <small>
                    {t.prefixo}… · criado em {t.criado_em}
                    {t.criado_por ? ` por ${t.criado_por}` : ""}
                  </small>
                </td>
                <td>{t.ultimo_uso || "nunca"}</td>
                <td>{Number(t.usos).toLocaleString("pt-BR")}</td>
                <td className="usuarios-acoes">
                  {t.ativo && (
                    <button type="button" onClick={() => revogar(t)}>
                      Revogar
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!tokens.length && (
              <tr>
                <td colSpan={4} className="metricas-vazio">
                  Nenhum token criado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <section className="metricas-bloco">
        <h2>Sites que usam a central</h2>
        <p className="metricas-legenda">
          Onde o chat (bolha e painéis) e o widget de dúvidas rodaram. Qualquer site funciona; site novo vira aviso no Slack.
          Bloqueie o que não for nosso.
        </p>
        <table className="metricas-tabela">
          <thead>
            <tr>
              <th>Site</th>
              <th>Último uso</th>
              <th>Conversas (30 dias)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sites.map((s) => (
              <tr key={s.dominio} className={s.bloqueado ? "usuarios-inativo" : ""}>
                <td>
                  {s.dominio.replace(/^https:\/\//, "")}
                  {s.bloqueado && <span className="metricas-selo">bloqueado</span>}
                  <small>
                    desde {s.primeiro_uso}
                    {s.bloqueado && s.bloqueado_por ? ` · bloqueado por ${s.bloqueado_por}` : ""}
                  </small>
                </td>
                <td>{s.ultimo_uso}</td>
                <td>{s.conversas.toLocaleString("pt-BR")}</td>
                <td className="usuarios-acoes">
                  <button type="button" onClick={() => alternarBloqueio(s)}>
                    {s.bloqueado ? "Desbloquear" : "Bloquear"}
                  </button>
                </td>
              </tr>
            ))}
            {!sites.length && (
              <tr>
                <td colSpan={4} className="metricas-vazio">
                  Nenhum site registrado ainda.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </>
  );
};

export default AdminIntegracoes;
